import React, { FC, useState } from 'react';
import cs from 'classnames';
import { useHref, useSearchParams } from 'react-router-dom';
import { Drawer } from '@wix/design-system';
import { ChevronDown, ChevronDownSmall } from '@wix/wix-ui-icons-common';
import { appRoutes } from '../../routes';
import { useSearch } from '../../hooks/useSearch';
import { useSelectedCategory } from '../../hooks/useSelectedCategory';
import { templateBuilderSwitchDataHooks as dataHooks } from './TemplateBuilderSwitch.dataHooks';
import s from './TemplateBuilderSwitch.scss';
import { useIsMobile } from '../../hooks/useIsMobile';

// Studio2 is the default when the spec is on; Studio is opted into via the query param
const STUDIO_QUERY_VALUE = 'Studio';

const useBuilderHref = (templateBuilders?: string) => {
  const [searchParams] = useSearchParams();
  const { isSearchActive, criteria } = useSearch();
  const selectedCategory = useSelectedCategory();

  const nextSearchParams = new URLSearchParams(searchParams);
  if (templateBuilders) {
    nextSearchParams.set('templateBuilders', templateBuilders);
  } else {
    nextSearchParams.delete('templateBuilders');
  }

  // drops the page: the other builder may have fewer pages
  const path = isSearchActive
    ? appRoutes.search.build({ criteria, searchParams: nextSearchParams })
    : appRoutes.category.build({
        slug: selectedCategory?.slug ?? '',
        searchParams: nextSearchParams,
      });

  return useHref(path);
};

export const TemplateBuilderSwitch: FC = () => {
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  // a bottom sheet opened by a tap on mobile, a dropdown opened on hover from tablet up
  const isMobile = useIsMobile();
  const studio2Href = useBuilderHref();
  const studioHref = useBuilderHref(STUDIO_QUERY_VALUE);

  const isStudioSelected =
    searchParams.get('templateBuilders') === STUDIO_QUERY_VALUE;

  const options = [
    { label: 'Studio 2.0', href: studio2Href, isSelected: !isStudioSelected },
    { label: 'Studio', href: studioHref, isSelected: isStudioSelected },
  ];

  const renderOptions = (className: string) => (
    <ul className={className}>
      {options.map(({ label, href, isSelected }) => (
        <li key={label}>
          <button
            className={cs(s.option, { [s.selected]: isSelected })}
            // full reload: templates for the selected builder are loaded on the server
            onClick={() => window.location.assign(href)}
          >
            {label}
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <div
      className={cs(s.container, { [s.open]: isOpen })}
      onPointerEnter={(e) =>
        !isMobile && e.pointerType === 'mouse' && setIsOpen(true)
      }
      onPointerLeave={(e) =>
        !isMobile && e.pointerType === 'mouse' && setIsOpen(false)
      }
    >
      <button
        className={s.trigger}
        onClick={(e) => {
          // the mouse already opened it on hover, a click shouldn't close it under the cursor
          const isMouseClick =
            (e.nativeEvent as PointerEvent).pointerType === 'mouse';
          setIsOpen(!isMobile && isMouseClick ? true : !isOpen);
        }}
        aria-expanded={isOpen}
        data-hook={dataHooks.trigger()}
      >
        {isStudioSelected ? 'Studio' : 'Studio 2.0'}
        {/* a medium button in the mobile bar, CSS picks the icon to avoid an SSR mismatch */}
        <ChevronDownSmall className={cs(s.chevron, s.desktopChevron)} />
        <ChevronDown className={cs(s.chevron, s.mobileChevron)} />
      </button>
      {renderOptions(s.menu)}
      {/* above the site header (z-index 9999) */}
      <Drawer
        open={isOpen && isMobile}
        onClose={() => setIsOpen(false)}
        zIndex={10000}
      >
        {renderOptions(s.sheetMenu)}
      </Drawer>
    </div>
  );
};
